import { ZonedDateTime } from '@js-joda/core';
import React from 'react';
import { type FieldValues, type Path, useController, type UseControllerProps } from 'react-hook-form';

import { SignatureField } from '#/shared/components/FormField';
import { type SignatureType } from '#/shared/components/SignatureInput';

type SignatureProps = Omit<
  React.ComponentProps<typeof SignatureField>,
  'ref' | 'disabled' | 'required' | 'value' | 'defaultValue' | 'onChange' | 'onBlur'
> & {
  signedAt?: ZonedDateTime;
  onSignAtChange: (signedAt: ZonedDateTime | undefined, signatureType: SignatureType | undefined) => void;
  'aria-label'?: string;
};

type FormControlSignatureProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues>> = UseControllerProps<
  TFieldValues,
  TName
>;

export function FormControlSignature<TFieldValues extends FieldValues, TName extends Path<TFieldValues>>({
  name,
  control,
  rules,
  defaultValue,
  disabled,
  signedAt,
  onSignAtChange,
  'aria-label': ariaLabel,
  ...props
}: FormControlSignatureProps<TFieldValues, TName> & SignatureProps) {
  const { field, fieldState } = useController({
    name,
    control,
    rules,
    defaultValue,
    disabled,
  });

  const id = React.useId();
  const inputId = `field-${id}`;

  return (
    <div className={'relative'}>
      {/* The input below is for form hooks to work */}
      {/* It has 0 height so it is not visible, but it allows it to be focused and scrolled to for validation purposes */}
      <input
        {...field}
        id={inputId}
        className={'absolute top-0 right-0 h-0'}
        aria-label={props.label ? undefined : ariaLabel}
      />
      <SignatureField
        {...props}
        labelFor={inputId}
        value={field.value}
        signedAt={signedAt}
        errorMessage={fieldState.error?.message}
        onChange={(value, signedAt, signatureType) => {
          field.onChange(value);
          onSignAtChange(signedAt, signatureType);
        }}
      />
    </div>
  );
}
